import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import BadgeIcon from "@material-ui/icons/AssignmentInd";
import SchoolIcon from "@material-ui/icons/School";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import { makeStyles } from "@material-ui/styles";
import managerUserApi from "api/managerUser";
import NO_AVATAR from "assets/img/no_avatar.png";
import AcademicRecord from "features/Home/pages/AcademicRecord";
import { InfoUser } from "models/auth";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { dateTimeToFormatDDMMYYYY } from "utils/dateTimeFormat";

export interface DetailUserProps {}

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: "0 24px",
    maxWidth: 1200,
    margin: "0 auto",
    boxSizing: "border-box",
    [theme.breakpoints?.down("md")]: {
      padding: "0 12px",
    },
  },
  headerBanner: {
    background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
    borderRadius: 24,
    padding: "24px 32px",
    color: "#FFFFFF",
    marginBottom: 24,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.15)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    [theme.breakpoints?.down("md")]: {
      padding: "16px 20px",
      borderRadius: 16,
      marginBottom: 16,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 300,
      height: 300,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
  },
  btnBack: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    color: "#FFFFFF",
    borderRadius: 12,
    padding: "8px 16px",
    fontWeight: 700,
    fontSize: "0.875rem",
    textTransform: "none",
    transition: "all 0.2s ease",
    zIndex: 2,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
      transform: "translateX(-2px)",
    },
  },
  bannerTitle: {
    color: "#FFFFFF",
    fontSize: "1.5rem",
    fontWeight: 800,
    zIndex: 2,
    [theme.breakpoints?.down("md")]: {
      fontSize: "1.125rem",
    },
  },
  heroCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    padding: 32,
    marginBottom: 24,
    boxShadow: "0 10px 30px -5px rgba(15, 23, 42, 0.06)",
    border: "1px solid #E2E8F0",
    display: "flex",
    gap: 32,
    alignItems: "center",
    [theme.breakpoints?.down("md")]: {
      flexDirection: "column",
      padding: 20,
      borderRadius: 16,
      gap: 20,
      textAlign: "center",
    },
  },
  avatarWrapper: {
    position: "relative",
    flexShrink: 0,
  },
  avatar: {
    width: 140,
    height: 140,
    borderRadius: 20,
    objectFit: "cover",
    border: "4px solid #F8FAFC",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
    [theme.breakpoints?.down("md")]: {
      width: 110,
      height: 110,
      borderRadius: 16,
    },
  },
  profileMain: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    flex: 1,
    width: "100%",
  },
  studentName: {
    fontSize: "1.5rem",
    fontWeight: 800,
    color: "#0F172A",
    [theme.breakpoints?.down("md")]: {
      fontSize: "1.25rem",
    },
  },
  chipContainer: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    [theme.breakpoints?.down("md")]: {
      justifyContent: "center",
    },
  },
  staffChip: {
    backgroundColor: "#F1F5F9",
    color: "#334155",
    fontWeight: 700,
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: "0.8125rem",
  },
  classChip: {
    backgroundColor: "#E0F2FE",
    color: "#0369A1",
    fontWeight: 700,
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: "0.8125rem",
  },
  infoGrid: {
    marginTop: 8,
  },
  infoCard: {
    backgroundColor: "#F8FAFC",
    borderRadius: 16,
    padding: "16px 20px",
    border: "1px solid #F1F5F9",
    display: "flex",
    alignItems: "center",
    gap: 16,
    height: "100%",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.05)",
      borderColor: "#E2E8F0",
    },
  },
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  infoLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 2,
  },
  infoValue: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: "#0F172A",
    wordBreak: "break-word",
  },
}));

export default function DetailUser(props: DetailUserProps) {
  const { id } = useParams();
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [detailUser, setDetailUser] = useState<InfoUser>();

  useEffect(() => {
    (async () => {
      try {
        const res: InfoUser = await managerUserApi.detailUser(id);
        setDetailUser(res);
      } catch (error) {
        console.error("Failed to fetch user details", error);
      }
    })();
  }, [id]);

  return (
    <Box className={classes.root}>
      {/* Header Banner */}
      <Box className={classes.headerBanner}>
        <Button
          startIcon={<ArrowBackIcon />}
          className={classes.btnBack}
          onClick={() => navigate(-1)}
        >
          Quay lại
        </Button>
        <Typography variant="h5" className={classes.bannerTitle}>
          Chi tiết học viên
        </Typography>
      </Box>

      {/* Hero Profile Card */}
      <Paper elevation={0} className={classes.heroCard}>
        <Box className={classes.avatarWrapper}>
          <img
            src={detailUser?.profileImageUrl || NO_AVATAR}
            alt="avatar"
            className={classes.avatar}
          />
        </Box>

        <Box className={classes.profileMain}>
          <Box>
            <Typography variant="h5" className={classes.studentName}>
              {detailUser?.name || "Chi tiết học viên"}
            </Typography>
          </Box>

          <Box className={classes.chipContainer}>
            {detailUser?.staffId && (
              <Chip
                label={`MNV: ${detailUser.staffId}`}
                size="small"
                className={classes.staffChip}
              />
            )}
            <Chip
              label={detailUser?.class?.name ? `Lớp: ${detailUser.class.name}` : "Chưa xếp lớp"}
              size="small"
              className={classes.classChip}
            />
          </Box>

          {/* Contact Details Grid */}
          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12} sm={6} md={6}>
              <Box className={classes.infoCard}>
                <Box className={classes.iconBox} style={{ backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                  <EmailIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>Email</Typography>
                  <Typography className={classes.infoValue}>
                    {detailUser?.email || "Chưa cập nhật"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box className={classes.infoCard}>
                <Box className={classes.iconBox} style={{ backgroundColor: "#F0FDF4", color: "#166534" }}>
                  <PhoneIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>Số điện thoại</Typography>
                  <Typography className={classes.infoValue}>
                    {detailUser?.phone || "Chưa cập nhật"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box className={classes.infoCard}>
                <Box className={classes.iconBox} style={{ backgroundColor: "#F8FAFC", color: "#475569" }}>
                  <BadgeIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>Mã nhân viên</Typography>
                  <Typography className={classes.infoValue}>
                    {detailUser?.staffId || "Chưa cập nhật"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Box className={classes.infoCard}>
                <Box className={classes.iconBox} style={{ backgroundColor: "#F0F9FF", color: "#0284C7" }}>
                  <SchoolIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography className={classes.infoLabel}>Lớp học</Typography>
                  <Typography className={classes.infoValue}>
                    {detailUser?.class?.name || "Chưa xếp lớp"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {detailUser?.dateOfJoining && (
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.infoCard}>
                  <Box className={classes.iconBox} style={{ backgroundColor: "#F5F3FF", color: "#7C3AED" }}>
                    <CalendarTodayIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography className={classes.infoLabel}>Ngày gia nhập</Typography>
                    <Typography className={classes.infoValue}>
                      {dateTimeToFormatDDMMYYYY(detailUser.dateOfJoining)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>

      {/* Academic Record Section */}
      <AcademicRecord id={id} />
    </Box>
  );
}
